"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function CollectionGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-square w-full rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-4 w-2/3 bg-muted/40 rounded-md animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="text-sm text-muted-foreground mb-4">
          Unable to load the holiday packages at the moment.
        </p>
        <Button variant="outline" onClick={refetch} className="border-border text-foreground hover:bg-accent rounded-lg">
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-16">
        <p className="text-muted-foreground font-default text-2xl italic">
          New travel packages arriving soon.
        </p>
      </div>;
  }
  const featuredProducts = products.slice(0, 5);
  const remainingProducts = products.slice(5);
  return <div className="space-y-20">
      {}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
        {featuredProducts.map((product, i) => <article data-index={i} key={product.id} className="group flex flex-col">
            <Link to={product.link} className="block overflow-hidden rounded-lg transition-all duration-500 ease-out hover:shadow-md">
              <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-muted">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-gradient-to-br from-muted to-accent" />}
                
                {product.discounted_price && <div className="absolute top-2 right-2 z-10">
                    <span className="bg-white text-primary text-[10px] font-bold uppercase px-2 py-1 rounded-sm shadow-sm">
                      Sale
                    </span>
                  </div>}
              </div>
            </Link>

            <div className="mt-3 px-1">
              <h3 className="font-default text-sm font-semibold text-foreground line-clamp-2 min-h-[2.5rem]">
                {product.title}
              </h3>
              <div className="mt-1">
                {product.price !== undefined && <p className="font-sans text-xs text-muted-foreground">
                    {product.discounted_price ? <>
                        <span className="line-through mr-1.5 opacity-60">
                          R{Number(product.price).toLocaleString()}
                        </span>
                        <span className="text-foreground font-bold">
                          R{Number(product.discounted_price).toLocaleString()}
                        </span>
                      </> : <span className="text-foreground font-bold">
                        R{Number(product.price).toLocaleString()}
                      </span>}
                  </p>}
              </div>
            </div>
          </article>)}
      </div>

      {}
      {remainingProducts.length > 0 && <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
          {remainingProducts.map((product, i) => {
        const duration = product.customFields?.duration || product.customFields?.nights || "7 Nights";
        const region = product.customFields?.region || product.customFields?.destination || product.categories && product.categories[0]?.name || "Global";
        const priceLabel = product.customFields?.departure && product.customFields?.price_from ? `${product.customFields.departure}  •  ${product.customFields.price_from}` : product.customFields?.departure ? `${product.customFields.departure}` : "2025  •  Best Value";
        return <article data-index={i + 5} key={product.id} className="group flex flex-col">
                <Link to={product.link} className="block overflow-hidden rounded-lg transition-all duration-700 ease-out group-hover:shadow-lg group-hover:-translate-y-1">
                  <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-muted">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" /> : <div className="h-full w-full bg-gradient-to-br from-muted to-accent" />}

                    {product.discounted_price && <div className="absolute top-4 right-4 z-10">
                        <span className="bg-primary text-primary-foreground text-[10px] font-bold uppercase px-3 py-1.5 rounded-sm shadow-lg">
                          Sale
                        </span>
                      </div>}

                    <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-background/90 via-background/50 to-transparent pointer-events-none" />

                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="font-default text-[10px] uppercase tracking-[0.18em] text-foreground/80 mb-2">
                        {priceLabel}
                      </p>
                      <h3 className="font-default text-2xl md:text-[1.625rem] leading-tight text-foreground font-semibold">
                        {product.title}
                      </h3>
                    </div>
                  </div>
                </Link>

                <div className="mt-5 flex items-start justify-between gap-4 px-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                      <span className="font-default italic text-foreground/90 text-base">
                        {region}
                      </span>
                      <span className="text-border">/</span>
                      <span className="font-default text-xs uppercase tracking-wider">
                        {duration}
                      </span>
                    </div>
                    {product.price !== undefined && <p className="mt-2 font-sans text-xl text-foreground font-bold">
                        {product.discounted_price ? <>
                            <span className="text-muted-foreground line-through mr-2 text-base font-normal">
                              R{Number(product.price).toLocaleString()}
                            </span>
                            R{Number(product.discounted_price).toLocaleString()}
                          </> : <>R{Number(product.price).toLocaleString()}</>}
                      </p>}
                  </div>

                  <Link to={product.link} className="inline-flex items-center gap-1.5 text-sm font-default uppercase tracking-wider text-primary hover:opacity-80 transition-all duration-300 whitespace-nowrap pt-1 group/link">
                    <span>Book</span>
                    <ArrowRight className="size-3.5 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                </div>

                <div className="mt-4">
                  <AddToCartButton productId={product.id} variant="default" size="sm" text="Reserve Package" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg font-sans" />
                </div>
              </article>;
      })}
        </div>}
    </div>;
}
export default function Destinations() {
  const wp_query_params = {
    per_page: 11,
    orderby: "popularity",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2967" id="europedestinationtabs" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-28">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-default text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                01 / Featured Holidays
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <h1 className="font-default font-bold text-foreground leading-[1.05] tracking-tight text-5xl md:text-6xl lg:text-[4rem]">
              Travel packages in{" "}
              <em className="italic font-normal text-primary">exclusive</em>{" "}
              destinations
            </h1>
          </div>

          <div className="lg:col-span-5 lg:pt-6 flex flex-col gap-8">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
              Explore our best-selling travel packages
            </p>

            <div>
              <Button asChild variant="outline" size="lg" className="rounded-lg border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground px-6 font-default text-xs uppercase tracking-wider">
                <Link to="/ShopPage">
                  <span>View All Offers</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 11,
        orderby: "popularity",
        order: "desc"
      }}>
          <CollectionGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}